import { modifier } from 'ember-modifier';
import * as powerbiClient from 'powerbi-client';
import {
  isDateRangeFilterRequired,
  type PowerBiPageRoute,
} from 'walden-fast/constants/power-bi';
import type { PowerBiReportInternalCode } from 'walden-fast/types/fast-backend/power-bi';
import type { FilterValues } from 'walden-fast/types/filters';
import { getFilterTables } from 'walden-fast/utils/powerbi-filter-helper';

interface EmbedPowerbiSignature {
  Element: HTMLDivElement;
  Args: {
    Positional: [string, string, string?];
    Named: {
      hideFilters?: boolean;
      filters?: FilterValues | null;
      internalCode?: PowerBiReportInternalCode;
      pageRoute?: PowerBiPageRoute;
    };
  };
}

const models = powerbiClient.models;
const powerbi = new powerbiClient.service.Service(
  powerbiClient.factories.hpmFactory,
  powerbiClient.factories.wpmpFactory,
  powerbiClient.factories.routerFactory,
);

function buildFilters(
  filters: FilterValues | null | undefined,
  internalCode?: PowerBiReportInternalCode,
  hasDateRangeFilter?: boolean,
): powerbiClient.models.ReportLevelFilters[] {
  if (!filters) return [];

  const filtersToSet: powerbiClient.models.ReportLevelFilters[] = [];

  // Date range filter
  if (
    hasDateRangeFilter &&
    filters.fromYear &&
    filters.fromMonth &&
    filters.toYear &&
    filters.toMonth
  ) {
    const fromDate = new Date(
      parseInt(filters.fromYear),
      parseInt(filters.fromMonth) - 1,
      1,
    ).toISOString();

    const toDate = new Date(
      parseInt(filters.toYear),
      parseInt(filters.toMonth),
      0,
    ).toISOString();

    const dateFilter: powerbiClient.models.IAdvancedFilter = {
      $schema: 'http://powerbi.com/product/schema#advanced',
      target: {
        table: '_Date',
        column: 'Date',
      },
      filterType: models.FilterType.Advanced,
      logicalOperator: 'And',
      conditions: [
        {
          operator: 'GreaterThanOrEqual',
          value: fromDate,
        },
        {
          operator: 'LessThanOrEqual',
          value: toDate,
        },
      ],
    };

    filtersToSet.push(dateFilter);
  }

  // Client filters
  if (filters.clients && filters.clients.length && internalCode) {
    const clientNameLocals = filters.clients
      .map((client) => client.externalIdentifiers?.powerBI?.clientNameLocal)
      .filter((name): name is string => Boolean(name));

    if (clientNameLocals.length > 0) {
      const tables = getFilterTables(internalCode, filters);

      tables.forEach((tableName) => {
        const clientFilter: powerbiClient.models.IBasicFilter = {
          $schema: 'http://powerbi.com/product/schema#basic',
          target: {
            table: tableName,
            column: 'Client Name Local',
          },
          operator: 'In',
          filterType: models.FilterType.Basic,
          values: clientNameLocals,
        };

        filtersToSet.push(clientFilter);
      });
    }
  }

  return filtersToSet;
}

// Store report instances to avoid re-embedding
const reportInstances = new WeakMap<
  HTMLElement,
  {
    report: powerbiClient.Report;
    reportId: string;
    token: string;
  }
>();

export default modifier<EmbedPowerbiSignature>(function embedPowerbi(
  element,
  [token, reportId, pageId],
  { hideFilters, filters, internalCode, pageRoute },
) {
  const hasDateRangeFilter = pageRoute
    ? isDateRangeFilterRequired(pageRoute)
    : true;

  const existingInstance = reportInstances.get(element);

  // If report already exists and reportId/token haven't changed, just update filters
  if (
    existingInstance &&
    existingInstance.reportId === reportId &&
    existingInstance.token === token
  ) {
    const newFilters = buildFilters(filters, internalCode, hasDateRangeFilter);
    void existingInstance.report.setFilters(newFilters).catch((error) => {
      console.error('Error updating PowerBI filters:', error);
    });

    return () => {
      // Don't reset on cleanup since we're reusing the instance
    };
  }

  // If we get here, we need to embed a new report
  const embedUrl = `https://app.powerbi.com/reportEmbed?reportId=${reportId}`;

  // Build initial filters

  const initialFilters = buildFilters(
    filters,
    internalCode,
    hasDateRangeFilter,
  );

  const embedConfig: powerbiClient.IReportEmbedConfiguration = {
    type: 'report',
    id: reportId,
    embedUrl,
    accessToken: token,
    tokenType: models.TokenType.Embed,
    permissions: models.Permissions.All,
    settings: {
      panes: {
        filters: {
          visible: !hideFilters,
        },
        pageNavigation: {
          visible: !pageId,
        },
      },
    },
    // Apply filters during initialization - mandatory to avoid data scope issues in certain situations
    filters: initialFilters,
    ...(pageId && { pageName: pageId }),
  };

  const report = powerbi.embed(element, embedConfig) as powerbiClient.Report;

  // Store the report instance
  reportInstances.set(element, {
    report,
    reportId,
    token,
  });

  return () => {
    reportInstances.delete(element);
    powerbi.reset(element);
  };
});
